import React, { createContext, useContext, useMemo, useState } from "react";

import type { Status } from "@/schemas";

type FeedStatusCount = {
  [feedId: string]: Status[];
};

const initialUnreadFeeds: FeedStatusCount = {
  following: [],
  groups: [],
  recommended: [],
};

const StatContext = createContext<any>({
  totalUnreadFeedStatuses: 0,
  unreadChatsCount: 0,
  unreadFeeds: initialUnreadFeeds,
  unreadAlertsCount: 0,
});

type IStatContext = {
  setUnreadChatsCount: React.Dispatch<React.SetStateAction<number>>;
  setUnreadFeeds: React.Dispatch<React.SetStateAction<FeedStatusCount>>;
  setUnreadAlertsCount: React.Dispatch<React.SetStateAction<number>>;
  totalUnreadFeedStatuses: number;
  unreadChatsCount: number;
  unreadFeeds: FeedStatusCount;
  unreadAlertsCount: number;
};

interface StatProviderProps {
  children: React.ReactNode;
}

const StatProvider: React.FC<StatProviderProps> = ({ children }) => {
  const [unreadFeeds, setUnreadFeeds] =
    useState<FeedStatusCount>(initialUnreadFeeds);
  const [unreadChatsCount, setUnreadChatsCount] = useState<number>(0);
  const [unreadAlertsCount, setUnreadAlertsCount] = useState<number>(0);

  const totalUnreadFeedStatuses = useMemo(() => {
    return Object.values(unreadFeeds).flat().length;
  }, [unreadFeeds]);

  const value = useMemo(
    () => ({
      setUnreadChatsCount,
      setUnreadFeeds,
      setUnreadAlertsCount,
      totalUnreadFeedStatuses,
      unreadChatsCount,
      unreadFeeds,
      unreadAlertsCount,
    }),
    [unreadChatsCount, unreadFeeds, unreadAlertsCount],
  );

  return <StatContext.Provider value={value}>{children}</StatContext.Provider>;
};

const useStatContext = (): IStatContext => useContext(StatContext);

export { StatProvider, useStatContext, type IStatContext };
